import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { api } from "@/services/api";
import { decodeRecordId, encodeRecordId } from "@/utils/record-token";

/**
 * Which record a list has open, kept in the address bar.
 *
 * Held in component state it could not tell a reload from arriving fresh, so a
 * screen reopened whatever was last looked at. In the URL, arriving has no
 * parameter and shows the list, reloading keeps it, and Back closes the panel.
 *
 * Several lists share one screen — the complimentary tab has a club list and
 * two reason lists — so the parameter names the list it belongs to:
 * `?edit=club.NDIuMWM4`. A list ignores a parameter that is not its own, which
 * is what stops one panel opening in three places at once.
 */
export function useRecordParam<T extends { id: number }>(
  key: "view" | "edit",
  /** Identifies this list among the ones sharing the screen. */
  listKey: string,
  /** Where to fetch a record the list does not already hold. */
  endpoint: string,
  /**
   * The rows the list is holding, so opening one does not read it again.
   *
   * These lists answer with the same schema for the list and for the record —
   * a province, a club, a reason is the same shape either way — so the read
   * returned a copy of what was already on screen and charged a spinner to do
   * it. Passing the rows skips both.
   *
   * Left out where a screen's detail is genuinely richer than its row, and the
   * deep-link path still reads: a pasted `?edit=` on a cold load has no list to
   * open from.
   *
   * Saving is safe either way — the row carries `updated_at`, it goes back as
   * `If-Match`, and a save from a copy that has since moved is refused with
   * 412 rather than quietly overwriting.
   */
  known?: readonly T[],
): {
  editing: "new" | T | null;
  setEditing: (next: "new" | T | null) => void;
  /** True while the record is being read. Nothing is open yet. */
  loading: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries()) as Record<string, unknown>;
  const [fetched, setFetched] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  // A ref because the effect below runs after the render that fills this, so
  // what it reads is the list as it is now rather than as it was when the
  // effect was declared.
  const rows = useRef<readonly T[]>([]);
  rows.current = known ?? [];

  const raw = typeof params[key] === "string" ? (params[key] as string) : undefined;
  const mine = raw?.startsWith(`${listKey}.`) ? raw.slice(listKey.length + 1) : undefined;
  const id = mine && mine !== "new" ? decodeRecordId(mine) : null;

  // `replace` so the panel opening and closing does not stack history entries —
  // Back still closes the panel because the token was pushed by whoever opened it.
  const setEditing = (next: "new" | T | null) =>
    setSearchParams(
      (previous) => {
        const search = new URLSearchParams(previous);
        if (next === null) search.delete(key);
        else search.set(key, `${listKey}.${next === "new" ? "new" : encodeRecordId(next.id)}`);
        return search;
      },
      { replace: true },
    );

  // Read the record, unless the list is already holding it.
  //
  // It used to read every time, on the grounds that a row is a projection and
  // as old as the last list fetch. Neither half holds here: these endpoints
  // answer with the same schema for the list and for one record, so the
  // request came back with what was already on screen, and staleness is what
  // `If-Match` is for — a save from a copy that has moved is refused rather
  // than quietly overwriting.
  //
  // So a row on hand opens straight away, with no spinner over the page. What
  // still reads is the case that has nothing to open from: a pasted `?edit=`,
  // a new tab, a reload with the panel open.
  useEffect(() => {
    if (id === null) {
      setFetched(null);
      setLoading(false);
      return;
    }
    const onHand = rows.current.find((row) => row.id === id);
    if (onHand) {
      setFetched(onHand);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setFetched(null);
    setLoading(true);
    void api
      .get<T>(`${endpoint}/${id}`)
      .then((response) => !cancelled && setFetched(response.data))
      .catch(() => !cancelled && setEditing(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, endpoint]);

  const editing: "new" | T | null = mine === "new" ? "new" : id === null ? null : fetched;
  return { editing, setEditing, loading: loading && mine !== "new" };
}
